import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A vertical stacked bar chart displays data using vertically aligned bars where each bar is
          divided into segments representing subcategories. The height of the entire bar reflects
          the total value, while the segments illustrate the contribution of each subcategory. This
          chart type is ideal for showing part-to-whole relationships and comparing cumulative
          values across categories or time periods.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">Vertical stacked bar charts are best suited for:</p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Visualizing Component Proportions:</strong> They are useful for showing how
            different parts contribute to a whole across categories, such as tracking sales by
            product category within each year or the breakdown of expenses by department.
          </li>
          <li>
            <strong>Displaying Cumulative Data:</strong> Vertical stacked bar charts help to
            visualize the cumulative value of multiple subcategories over time, like monthly
            revenue, where each segment represents a different source of income.
          </li>
          <li>
            <strong>Comparing Relative Contributions Across Categories:</strong> They allow for easy
            comparison of the composition of each category, such as visualizing the market share of
            different brands within each region, where the size of each segment represents the
            relative contribution.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Vertical stacked bar charts are a valuable tool for visualizing the composition of data
          across categories, but they have certain limitations that should be considered:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficulty in Comparing Subcategories:</strong> Stacked bars make it challenging
            to compare individual subcategories across different bars, as the subcategory values are
            stacked on top of each other, making them harder to isolate visually.
          </li>
          <li>
            <strong>Limited Precision for Small Changess:</strong> When there are small differences
            in the size of the subcategories, vertical stacked bar charts can fail to accurately
            represent those changes, leading to potential misinterpretations.
          </li>
          <li>
            <strong>Cluttered Display with Many Categories:</strong> As the number of categories or
            subcategories increases, the chart can become cluttered, which reduces its readability
            and makes it harder to focus on the most important data points.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection is essential in vertical stacked bar charts to clearly represent
          subcategories and their contributions to the whole. Using a distinct palette for each
          subcategory ensures visual differentiation, while harmonious color schemes maintain a
          cohesive look. Gradual gradients or soft tones can highlight cumulative trends, while bold
          contrasts emphasize specific data segments. Thoughtful color choices enhance readability
          and help the viewer quickly grasp the chart&apos;s overall structure and details.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative', 'sequential', 'diverging']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Vertical stacked bar charts are useful for visualizing the composition of data, but they
          present several challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficulty in Comparing Subcategories:</strong> The stacked design makes it hard
            to compare the size of individual subcategories across different bars, especially when
            values are similar, leading to less precision.
          </li>
          <li>
            <strong>Overlapping Data in Small Categories:</strong> Smaller subcategories can be hard
            to distinguish, especially in bars with large differences in magnitude, potentially
            obscuring important details.
          </li>
          <li>
            <strong>Cluttered Appearance with Many Categories:</strong> As the number of categories
            or subcategories increases, the chart can become cluttered and harder to read, reducing
            the chart’s effectiveness in conveying key insights.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
